import { AppState } from 'react-native';
import { Store } from '../../reducers/store';
import { 
    setRaspStatus, 
    setRaspThreatLevel, 
    addRaspSecurityEvent,
    getRaspDetectionEnabled 
} from '../../reducers/configSlice';
import { isWEB } from '../helpers/getPlatformType';
import { EventBus } from '../EventBus';
import { showToastMessage } from '../toastMessage';
import {
    RASP_SERVICE_STATUS,
    THREAT_LEVELS,
    SECURITY_EVENT_TYPES,
    THREAT_RESPONSE_MAP,
    EVENT_SEVERITY_MAP,
    DETECTION_CONFIG,
    SECURITY_MESSAGES,
    DETECTION_THRESHOLDS
} from './constants';
import logger from './logger';

// Import detectors
import { RuntimeTamperingDetector } from './detectors/runtimeTamperingDetector';
import { ApiAbuseDetector } from './detectors/apiAbuseDetector';
import { MemoryIntegrityDetector } from './detectors/memoryIntegrityDetector';
import { NetworkSecurityDetector } from './detectors/networkSecurityDetector';
import { DeviceEnvironmentDetector } from './detectors/deviceEnvironmentDetector';
import { AppStateIntegrityDetector } from './detectors/appStateIntegrityDetector';

/**
 * Runtime Application Self-Protection (RASP) Service
 * 
 * Provides real-time protection against runtime attacks and tampering
 * by monitoring app behavior, API usage, memory integrity, and device environment.
 */
class RaspService {
    constructor() {
        this.status = RASP_SERVICE_STATUS.UNINITIALIZED;
        this.currentThreatLevel = THREAT_LEVELS.NONE;
        this.detectors = new Map();
        this.eventHistory = [];
        this.monitoringIntervals = new Map();
        this.appStateSubscription = null;
        this.lastSecurityCheck = Date.now();
        
        // Performance tracking
        this.activeChecks = 0;
        this.checkQueue = [];
    }

    /**
     * Initialize the RASP service
     * Sets up all security detectors and monitoring systems
     */
    async initialize() {
        if (this.status === RASP_SERVICE_STATUS.ACTIVE) {
            logger.debug('Service already initialized');
            return true;
        }

        if (isWEB()) {
            logger.info('Web platform detected, limited functionality');
            // Web has limited RASP capabilities but still provide basic monitoring
        }

        try {
            this.status = RASP_SERVICE_STATUS.INITIALIZING;
            Store.dispatch(setRaspStatus(this.status));

            logger.service('initializing');

            // Initialize detectors
            await this.initializeDetectors();
            
            // Set up monitoring intervals
            this.setupMonitoring();
            
            // Set up app state monitoring
            this.setupAppStateMonitoring();
            
            // Set up event bus listeners
            this.setupEventListeners();

            this.status = RASP_SERVICE_STATUS.ACTIVE;
            Store.dispatch(setRaspStatus(this.status));

            logger.service('initialized');
            return true;

        } catch (error) {
            this.status = RASP_SERVICE_STATUS.ERROR;
            Store.dispatch(setRaspStatus(this.status));

            logger.service('failed', error);
            return false;
        }
    }

    /**
     * Initialize all security detectors
     */
    async initializeDetectors() {
        const detectorConfigs = [
            { name: 'runtimeTampering', class: RuntimeTamperingDetector, enabled: DETECTION_CONFIG.RUNTIME_TAMPERING_ENABLED },
            { name: 'apiAbuse', class: ApiAbuseDetector, enabled: DETECTION_CONFIG.API_ABUSE_ENABLED },
            { name: 'memoryIntegrity', class: MemoryIntegrityDetector, enabled: DETECTION_CONFIG.MEMORY_INTEGRITY_ENABLED },
            { name: 'networkSecurity', class: NetworkSecurityDetector, enabled: DETECTION_CONFIG.NETWORK_SECURITY_ENABLED },
            { name: 'deviceEnvironment', class: DeviceEnvironmentDetector, enabled: DETECTION_CONFIG.DEVICE_ENVIRONMENT_ENABLED },
            { name: 'appStateIntegrity', class: AppStateIntegrityDetector, enabled: DETECTION_CONFIG.APP_STATE_INTEGRITY_ENABLED }
        ];

        for (const config of detectorConfigs) {
            if (config.enabled) {
                try {
                    const detector = new config.class(this.onSecurityEvent.bind(this));
                    await detector.initialize();
                    this.detectors.set(config.name, detector);
                    logger.detector(config.name, 'initialized');
                } catch (error) {
                    logger.detector(config.name, 'failed', error);
                }
            }
        }
    }

    /**
     * Set up monitoring intervals for periodic security checks
     */
    setupMonitoring() {
        // Memory integrity checks (LESS FREQUENT)
        if (this.detectors.has('memoryIntegrity')) {
            const memoryInterval = setInterval(() => {
                this.performSecurityCheck('memoryIntegrity');
            }, DETECTION_THRESHOLDS.MEMORY_CHECK_INTERVAL); // Now 5 minutes
            this.monitoringIntervals.set('memory', memoryInterval);
        }

        // Network security checks (LESS FREQUENT)
        if (this.detectors.has('networkSecurity')) {
            const networkInterval = setInterval(() => {
                this.performSecurityCheck('networkSecurity');
            }, DETECTION_THRESHOLDS.NETWORK_CHECK_INTERVAL); // Now 5 minutes
            this.monitoringIntervals.set('network', networkInterval);
        }

        // App state integrity checks (LESS FREQUENT)
        if (this.detectors.has('appStateIntegrity')) {
            const stateInterval = setInterval(() => {
                this.performSecurityCheck('appStateIntegrity');
            }, DETECTION_THRESHOLDS.STATE_CHECK_INTERVAL); // Now 1 minute
            this.monitoringIntervals.set('state', stateInterval);
        }

        // Cleanup interval for event history
        const cleanupInterval = setInterval(() => {
            this.cleanupEventHistory();
        }, DETECTION_CONFIG.CLEANUP_INTERVAL);
        this.monitoringIntervals.set('cleanup', cleanupInterval);
    }

    /**
     * Set up app state monitoring for security events
     */
    setupAppStateMonitoring() {
        this.appStateSubscription = AppState.addEventListener('change', (nextAppState) => {
            this.handleAppStateChange(nextAppState);
        });
    }

    /**
     * Set up event bus listeners for security-related events
     */
    setupEventListeners() {
        // Listen for API calls to monitor for abuse
        EventBus.on('api_call', (data) => {
            if (this.detectors.has('apiAbuse')) {
                this.detectors.get('apiAbuse').monitorApiCall(data);
            }
        });

        // Listen for Soketi events
        EventBus.on('soketi_event', (data) => {
            if (this.detectors.has('networkSecurity')) {
                this.detectors.get('networkSecurity').monitorSocketEvent(data);
            }
        });

        // Listen for configuration changes
        EventBus.on('config_updated', (data) => {
            if (this.detectors.has('appStateIntegrity')) {
                this.detectors.get('appStateIntegrity').monitorConfigChange(data);
            }
        });
    }

    /**
     * Perform a security check with a specific detector
     */
    async performSecurityCheck(detectorName) {
        if (!this.isDetectionEnabled() || this.activeChecks >= DETECTION_CONFIG.MAX_CONCURRENT_CHECKS) {
            return;
        }

        const detector = this.detectors.get(detectorName);
        if (!detector) return;

        this.activeChecks++;
        
        try {
            const timeout = new Promise((_, reject) => 
                setTimeout(() => reject(new Error('Security check timeout')), DETECTION_CONFIG.CHECK_TIMEOUT)
            );
            
            await Promise.race([detector.performCheck(), timeout]);
        } catch (error) {
            console.warn(`RASP - Security check failed for ${detectorName}:`, error);
        } finally {
            this.activeChecks--;
            this.processCheckQueue();
        }
    }

    /**
     * Process queued security checks
     */
    processCheckQueue() {
        if (this.checkQueue.length > 0 && this.activeChecks < DETECTION_CONFIG.MAX_CONCURRENT_CHECKS) {
            const nextCheck = this.checkQueue.shift();
            this.performSecurityCheck(nextCheck);
        }
    }

    /**
     * Handle app state changes for security monitoring
     */
    handleAppStateChange(nextAppState) {
        // Monitor for suspicious app state transitions
        if (this.detectors.has('runtimeTampering')) {
            this.detectors.get('runtimeTampering').monitorAppStateChange(nextAppState);
        }
    }

    /**
     * Handle security events from detectors
     */
    onSecurityEvent(eventType, eventData) {
        try {
            const severity = EVENT_SEVERITY_MAP[eventType] || THREAT_LEVELS.LOW;

            // Log the security event using smart logger
            const eventWithSeverity = {
                ...eventData,
                severity
            };

            logger.securityEvent(eventType, eventWithSeverity);

            // Store in Redux
            this.logSecurityEvent(eventType, eventWithSeverity);

            // Update threat level if necessary
            const previousLevel = this.currentThreatLevel;
            this.updateThreatLevel(severity);

            // Log threat level change
            if (previousLevel !== this.currentThreatLevel) {
                logger.threatLevel(this.currentThreatLevel, previousLevel);
            }

            // Execute response actions
            this.executeResponseActions(severity, eventType, eventData);

        } catch (error) {
            logger.error('Error handling security event:', error);
        }
    }

    /**
     * Log a security event
     */
    logSecurityEvent(eventType, eventData) {
        const event = {
            type: eventType,
            timestamp: new Date().toISOString(),
            ...eventData
        };

        // Add to Redux store
        Store.dispatch(addRaspSecurityEvent(event));

        // Add to local history
        this.eventHistory.push(event);
    }

    /**
     * Update the current threat level
     */
    updateThreatLevel(newSeverity) {
        const severityLevels = Object.values(THREAT_LEVELS);
        const currentIndex = severityLevels.indexOf(this.currentThreatLevel);
        const newIndex = severityLevels.indexOf(newSeverity);

        if (newIndex > currentIndex) {
            this.currentThreatLevel = newSeverity;
            Store.dispatch(setRaspThreatLevel(newSeverity));
        }
    }

    /**
     * Execute response actions based on threat level
     */
    executeResponseActions(severity, eventType, eventData) {
        const actions = THREAT_RESPONSE_MAP[severity] || [];

        actions.forEach(action => {
            try {
                switch (action) {
                    case 'notify_user':
                        this.notifyUser(severity, eventType);
                        break;
                    case 'report_backend':
                        // Backend reporting disabled - RASP works without backend
                        console.log('RASP - Backend reporting disabled (frontend-only mode)');
                        break;
                    case 'degrade_functionality':
                        this.degradeFunctionality(severity);
                        break;
                    case 'force_reconnect':
                        this.forceReconnect();
                        break;
                }
            } catch (error) {
                logger.error(`Failed to execute response action ${action}:`, error);
            }
        });
    }

    /**
     * Notify user of security threat (DISABLED - RASP notifications hidden from users)
     */
    notifyUser(severity, eventType) {
        // RASP toast notifications are hidden from users
        // const message = `${SECURITY_MESSAGES.THREAT_DETECTED}: ${eventType}`;
        // const duration = severity === THREAT_LEVELS.CRITICAL ? -1 : 10000;
        // showToastMessage(message, duration);
        return;
    }

    /**
     * Report security event to backend (DISABLED - Frontend-only mode)
     */
    reportToBackend(eventType, eventData) {
        // Backend reporting disabled - RASP works completely frontend-only
        console.log('RASP - Security event (backend reporting disabled):', {
            type: eventType,
            data: eventData,
            timestamp: new Date().toISOString()
        });
    }

    /**
     * Degrade functionality in response to threats
     */
    degradeFunctionality(severity) {
        if (severity === THREAT_LEVELS.HIGH || severity === THREAT_LEVELS.CRITICAL) {
            // Could disable certain features, increase security checks, etc.
            logger.warn('Degrading functionality due to security threat');
        }
    }

    /**
     * Force reconnection to reset potential compromised connections
     */
    forceReconnect() {
        logger.warn('Forcing reconnection due to security threat');
        EventBus.emit('force_reconnect');
    }

    /**
     * Check if detection is currently enabled
     */
    isDetectionEnabled() {
        const state = Store.getState();
        return getRaspDetectionEnabled(state);
    }

    /**
     * Clean up old events from history
     */
    cleanupEventHistory() {
        const maxEvents = DETECTION_CONFIG.MAX_EVENT_HISTORY;
        if (this.eventHistory.length > maxEvents) {
            this.eventHistory = this.eventHistory.slice(-maxEvents);
        }
    }

    /**
     * Get current security status
     */
    getSecurityStatus() {
        return {
            status: this.status,
            threatLevel: this.currentThreatLevel,
            activeDetectors: Array.from(this.detectors.keys()),
            eventCount: this.eventHistory.length,
            lastCheck: this.lastSecurityCheck
        };
    }

    /**
     * Cleanup the RASP service
     */
    cleanup() {
        try {
            // Clear all intervals
            this.monitoringIntervals.forEach(interval => clearInterval(interval));
            this.monitoringIntervals.clear();

            // Remove app state listener
            if (this.appStateSubscription) {
                this.appStateSubscription.remove();
                this.appStateSubscription = null;
            }

            // Cleanup detectors
            this.detectors.forEach(detector => {
                if (detector.cleanup) {
                    detector.cleanup();
                }
            });
            this.detectors.clear();

            // Remove event listeners
            EventBus.removeAllListeners('api_call');
            EventBus.removeAllListeners('soketi_event');
            EventBus.removeAllListeners('config_updated');

            this.status = RASP_SERVICE_STATUS.UNINITIALIZED;
            Store.dispatch(setRaspStatus(this.status));

            logger.service('cleanup');
        } catch (error) {
            logger.error('Error during cleanup:', error);
        }
    }
}

// Create singleton instance
const raspService = new RaspService();

/**
 * Initialize RASP protection with startup delay
 */
export const initializeRaspProtection = async () => {
    // Add a small delay to allow app to initialize properly
    // This prevents false positives during app startup
    setTimeout(async () => {
        await raspService.initialize();
    }, 3000); // 3 second delay

    return true; // Return immediately, actual init happens after delay
};

/**
 * Cleanup RASP protection
 */
export const cleanupRaspProtection = () => {
    raspService.cleanup();
};

/**
 * Get RASP service instance for advanced usage
 */
export const getRaspService = () => raspService;

export default raspService;
